import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CodeGenerator.css';
import InputMethodTabs from './InputMethodTabs';
import { generateCode, getModelsConfig } from '../../../services/codegen.service';
import { showSuccess, showError } from '../../../services/notification.service';

function CodeGenerator() {
  const navigate = useNavigate();
  const [inputMethod, setInputMethod] = useState('text'); // 'text', 'api', or 'file'
  const [generationType, setGenerationType] = useState('full'); // 'full' or 'utility'
  const [requirements, setRequirements] = useState('');
  const [language, setLanguage] = useState('python');
  const [framework, setFramework] = useState('');
  const [model, setModel] = useState('');
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // API Endpoints state
  const [apiEndpoints, setApiEndpoints] = useState([{
    method: 'POST',
    url: '',
    auth: '',
    headers: '',
    body: '',
    expectedResponse: '',
    description: ''
  }]);

  // Enhanced layers with auth, deployment, and test levels
  const [selectedLayers, setSelectedLayers] = useState({
    auth: true,
    validation: true,
    business: true,
    dataAccess: true,
    api: true,
    deployment: false,
    tests: true,
    testL1: true,  // Unit tests
    testL2: true   // Integration tests
  });
  
  const [isGenerating, setIsGenerating] = useState(false);

  const languages = [
    { value: 'python', label: 'Python' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'java', label: 'Java' },
    { value: 'go', label: 'Go' },
    { value: 'csharp', label: 'C#' }
  ];

  const frameworks = {
    python: ['FastAPI', 'Flask', 'Django'],
    typescript: ['Express', 'NestJS', 'React'],
    java: ['Spring Boot', 'Quarkus', 'Micronaut'],
    go: ['Gin', 'Echo', 'Fiber'],
    csharp: ['.NET Core', 'ASP.NET']
  };

  // Load models configuration on mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        setModelsLoading(true);
        const data = await getModelsConfig();
        const modelsList = data.models || [];
        setModels(modelsList);
        
        // Set default model
        if (data.default_model) {
          setModel(data.default_model);
        } else if (modelsList.length > 0) {
          setModel(modelsList[0].id);
        }
      } catch (error) {
        console.error('Failed to load models:', error);
        showError('Failed to load AI models');
        // Fallback to a default model
        setModels([{
          model_id: 'us.meta.llama3-3-70b-instruct-v1:0',
          name: 'Llama 3.3 70B (Meta)',
          provider: 'Meta'
        }]);
        setModel('us.meta.llama3-3-70b-instruct-v1:0');
      } finally {
        setModelsLoading(false);
      }
    };
    
    loadModels();
  }, []);

  // API Endpoint handlers
  const handleAddEndpoint = () => {
    if (apiEndpoints.length < 5) {
      setApiEndpoints([...apiEndpoints, {
        method: 'POST',
        url: '',
        auth: '',
        headers: '',
        body: '',
        expectedResponse: '',
        description: ''
      }]);
    }
  };

  const handleRemoveEndpoint = (index) => {
    if (apiEndpoints.length > 1) {
      setApiEndpoints(apiEndpoints.filter((_, i) => i !== index));
    }
  };

  const handleEndpointUpdate = (index, updatedEndpoint) => {
    const newEndpoints = [...apiEndpoints];
    newEndpoints[index] = updatedEndpoint;
    setApiEndpoints(newEndpoints);
  };

  const handleLayerToggle = (layer) => {
    setSelectedLayers(prev => ({
      ...prev,
      [layer]: !prev[layer]
    }));
  };

  const handleGenerate = async () => {
    // Validate based on input method
    if (inputMethod === 'text' && !requirements.trim()) {
      showError('Please enter requirements');
      return;
    }
    if (inputMethod === 'api' && apiEndpoints.some(e => !e.url.trim())) {
      showError('Please fill in all endpoint URLs');
      return;
    }
    if (!model || model.trim() === '') {
      showError('Please select an AI model. Models are loading...');
      return;
    }

    setIsGenerating(true);
    
    try {
      // Prepare request based on input method
      let requestData = {
        generation_type: generationType,
        language,
        framework,
        model_id: model,
        layers: selectedLayers
      };

      if (inputMethod === 'text') {
        requestData.requirements = requirements;
      } else if (inputMethod === 'api') {
        requestData.api_specifications = apiEndpoints;
        // Generate requirements summary from API specs
        requestData.requirements = `API Specification:\n${apiEndpoints.map((ep, i) => 
          `\nEndpoint ${i + 1}:\nMethod: ${ep.method}\nURL: ${ep.url}\nDescription: ${ep.description}`
        ).join('\n')}`;
      } else if (inputMethod === 'file') {
        requestData.requirements = requirements;
      }

      const response = await generateCode(requestData);

      if (response.success && response.job_id) {
        showSuccess('Code generation job submitted! Check the Jobs tab for progress.');
        navigate('/tools/code-generator/jobs');
      } else {
        throw new Error(response.error || 'Failed to queue code generation job');
      }
    } catch (error) {
      console.error('Code generation failed:', error);
      showError(error.response?.data?.error || error.message || 'Failed to generate code. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="code-generator-container">
      <div className="code-generator-content-wrapper">
        
        {/* LEFT COLUMN - Settings (Sticky) */}
        <div className="left-column">
          <div className="settings-section">
            <div className="section-header">
              <h2>⚙️ Configuration</h2>
            </div>

            {/* Generation Type */}
            <div className="generation-type">
              <label>Generation Type:</label>
              <div className="type-radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    value="full"
                    checked={generationType === 'full'}
                    onChange={(e) => setGenerationType(e.target.value)}
                  />
                  <span>Full Application</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    value="utility"
                    checked={generationType === 'utility'}
                    onChange={(e) => setGenerationType(e.target.value)}
                  />
                  <span>Utility/Function Only</span>
                </label>
              </div>
            </div>

            {/* Language and Framework Selection - Side by Side */}
            <div className="config-row">
              <div className="config-field">
                <label>Programming Language:</label>
                <select
                  value={language}
                  onChange={(e) => {
                    setLanguage(e.target.value);
                    setFramework('');
                  }}
                  className="config-select"
                >
                  {languages.map(lang => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="config-field">
                <label>Framework (Optional):</label>
                <select
                  value={framework}
                  onChange={(e) => setFramework(e.target.value)}
                  className="config-select"
                >
                  <option value="">Select framework...</option>
                  {frameworks[language]?.map(fw => (
                    <option key={fw} value={fw}>
                      {fw}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Model Selection */}
            <div className="config-field">
              <label>AI Model:</label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="config-select"
                disabled={modelsLoading}
              >
                {modelsLoading ? (
                  <option value="">Loading models...</option>
                ) : models.length === 0 ? (
                  <option value="">No models available</option>
                ) : (
                  models.map(m => (
                    <option key={m.name} value={m.id}>
                      {m.name} ({m.provider})
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Code Layers */}
            <div className="layers-section">
              <label className="section-label">Code Layers to Generate:</label>
              <div className="layers-grid">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={selectedLayers.auth}
                    onChange={() => handleLayerToggle('auth')}
                    disabled={generationType === 'utility'}
                  />
                  <span>Auth Layer</span>
                </label>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={selectedLayers.validation}
                    onChange={() => handleLayerToggle('validation')}
                  />
                  <span>Validation Layer</span>
                </label>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={selectedLayers.business}
                    onChange={() => handleLayerToggle('business')}
                  />
                  <span>Business Layer</span>
                </label>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={selectedLayers.dataAccess}
                    onChange={() => handleLayerToggle('dataAccess')}
                    disabled={generationType === 'utility'}
                  />
                  <span>Data Layer</span>
                </label>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={selectedLayers.api}
                    onChange={() => handleLayerToggle('api')}
                    disabled={generationType === 'utility'}
                  />
                  <span>API Layer</span>
                </label>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={selectedLayers.deployment}
                    onChange={() => handleLayerToggle('deployment')}
                    disabled={generationType === 'utility'}
                  />
                  <span>Deployment Layer</span>
                </label>
              </div>

              {/* Test Layers - Nested */}
              <div className="test-layers">
                <label className="checkbox-label main-test">
                  <input 
                    type="checkbox" 
                    checked={selectedLayers.tests}
                    onChange={() => handleLayerToggle('tests')}
                  />
                  <span>Test Layer</span>
                </label>
                {selectedLayers.tests && (
                  <div className="test-levels">
                    <label className="checkbox-label sub-test">
                      <input 
                        type="checkbox" 
                        checked={selectedLayers.testL1}
                        onChange={() => handleLayerToggle('testL1')}
                      />
                      <span>L1 - Unit Tests</span>
                    </label>
                    <label className="checkbox-label sub-test">
                      <input 
                        type="checkbox" 
                        checked={selectedLayers.testL2}
                        onChange={() => handleLayerToggle('testL2')}
                      />
                      <span>L2 - Integration Tests</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <button
              className="btn-generate"
              onClick={handleGenerate}
              disabled={isGenerating || modelsLoading || !model}
            >
              {modelsLoading ? '⏳ Loading models...' : isGenerating ? '⏳ Submitting Job...' : '🚀 Generate Code'}
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN - Input Methods (Scrollable) */}
        <div className="right-column">
          <div className="section-header">
            <h2>📝 Requirements Input</h2>
          </div>
          
          <InputMethodTabs
            activeTab={inputMethod}
            onTabChange={setInputMethod}
            requirements={requirements}
            onRequirementsChange={setRequirements}
            uploadedFile={uploadedFile}
            onFileUpload={setUploadedFile}
            apiEndpoints={apiEndpoints}
            onEndpointUpdate={handleEndpointUpdate}
            onEndpointRemove={handleRemoveEndpoint}
            onAddEndpoint={handleAddEndpoint}
            maxEndpoints={5}
          />
        </div>

      </div>
    </div>
  );
}

export default CodeGenerator;
